/**
 * "Season vs last year" (CU-869fmhmx8 D7): the server hands back per provider and column the stats of both periods;
 * this composes the table — the value of each cell, the change, the market median, your position, the cheapest,
 * coverage and who moved most. Min / Avg / Max, sorting and searching never ask the server again.
 *
 * Pure and alias-free: `node --test` runs it.
 */

// CA 14: fewer prices than this in either period marks the cell
export const THIN_PRICES = 5

// the median and the ranking are the same in every dashboard (CU-869fmjzrh S2)
import { isPrice, median, rankOf } from '../../../@services/stats.js'

export { median }

const minOf = xs => (xs.length ? Math.min(...xs) : null)
const maxOf = xs => (xs.length ? Math.max(...xs) : null)
const defined = xs => xs.filter(x => x !== null && x !== undefined)

/** One cell for the chosen statistic: `{ cur, prev, val, prevVal, de, dp, why, thin }` */
export function cellOf(raw, stat) {
	const cur = raw?.cur && raw.cur[stat] !== null && raw.cur[stat] !== undefined ? raw.cur : null
	const prev = cur && raw?.prev && raw.prev[stat] !== null && raw.prev[stat] !== undefined ? raw.prev : null
	const cell = { cur, prev, val: cur ? cur[stat] : null, prevVal: prev ? prev[stat] : null, de: null, dp: null, why: raw?.why || null, thin: false }
	if (cur && prev) {
		cell.de = cell.val - cell.prevVal
		cell.dp = cell.prevVal ? cell.de / cell.prevVal : null
		cell.why = null
	} else if (cur && !cell.why) {
		cell.why = 'nodata'
	}
	cell.thin = !!cur && (cur.n < THIN_PRICES || (!!prev && prev.n < THIN_PRICES))
	return cell
}

function aggregate(stats, values) {
	return {
		min: minOf(defined(stats.map(s => s.min))),
		avg: null,
		max: maxOf(defined(stats.map(s => s.max))),
		median: median(stats.map(s => s.median)),
		n: stats.reduce((sum, s) => sum + (s.n || 0), 0),
		captureDays: null,
		firstCapture: defined(stats.map(s => s.firstCapture)).sort()[0] || null,
		lastCapture: defined(stats.map(s => s.lastCapture)).sort().at(-1) || null,
		value: median(values),
	}
}

/**
 * Market median of a column (CA 11): the median of the competitors' values (no primary brand, no other own brands;
 * without identity, of everyone). Compared only when at least two competitors have both periods.
 */
export function medianCell(competitorCells, compare) {
	const withCur = competitorCells.filter(c => c.cur)
	if (!withCur.length) return { cur: null, prev: null, val: null, prevVal: null, de: null, dp: null, why: null, thin: false }

	const cur = aggregate(withCur.map(c => c.cur), withCur.map(c => c.val))
	const both = withCur.filter(c => c.prev)
	const prev = both.length >= 2 ? aggregate(both.map(c => c.prev), both.map(c => c.prevVal)) : null

	const cell = { cur, prev, val: cur.value, prevVal: prev ? prev.value : null, de: null, dp: null, why: null, thin: false }
	if (prev) {
		cell.de = cell.val - cell.prevVal
		cell.dp = cell.prevVal ? cell.de / cell.prevVal : null
	} else {
		cell.why = compare !== 'ly' && competitorCells.some(c => c.why === 'passed') ? 'passed' : 'nodata'
	}
	cell.thin = cur.n < THIN_PRICES || (!!prev && prev.n < THIN_PRICES)
	return cell
}

const byValue = (a, b) => a.v - b.v || String(a.row.provider.name).localeCompare(String(b.row.provider.name))

/**
 * Your position and the cheapest of a column (CA 13, UX Obs. 5): position among the primary brand and the
 * competitors (other own brands out), 1 = the cheapest, ties share it; cheapest among everyone with a price.
 */
export function standing(rows, j, you) {
	// a cell with stats but no value for the statistic is not priced (CU-869fmjzrh S2 QA: it must not break the column)
	const priced = rows.filter(r => r.cells[j].cur && isPrice(r.cells[j].val)).map(row => ({ row, v: row.cells[j].val })).sort(byValue)
	const ranked = priced.filter(x => x.row.provider.own !== 'other')
	const cheapest = priced[0] || null

	let pos = null, gap = null
	const total = ranked.length
	if (you && you.cells[j].cur && isPrice(you.cells[j].val)) {
		const yv = you.cells[j].val
		pos = rankOf(ranked.map(x => x.v), yv)
		const others = ranked.filter(x => x.row !== you)
		if (pos === 1) {
			const second = others[0]
			gap = { first: true, e: second ? second.v - yv : null, p: second ? (second.v - yv) / second.v : null, who: second ? second.row.provider.name : null }
		} else {
			const best = others[0]
			gap = { first: false, e: yv - best.v, p: (yv - best.v) / best.v, who: best.row.provider.name }
		}
	}
	return { cheapest, pos, total, gap }
}

/** CA 15c: biggest rise and biggest drop of the column among the providers with both periods */
export function movers(rows, j) {
	const moved = rows.filter(r => r.cells[j].cur && r.cells[j].prev).map(r => ({ provider: r.provider, e: r.cells[j].de, p: r.cells[j].dp, thin: r.cells[j].thin }))
	const up = [...moved].sort((a, b) => b.e - a.e)[0]
	const down = [...moved].sort((a, b) => a.e - b.e)[0]
	return {
		rise: up && Math.round(up.e) > 0 ? up : null,
		drop: down && Math.round(down.e) < 0 ? down : null,
	}
}

/**
 * The whole table for a statistic. `visible` (E3, Curro's rule): the providers Tiers / Providers leave in sight, a Set
 * or a list of ids; the rows, the market median, your position, the cheapest and who moved most are computed among
 * them, your brand always in. `null` = everyone, as before the filters.
 */
export function buildModel(data, stat = 'min', visible = null) {
	const columns = data?.columns || []
	const identity = data?.identity || null
	const compare = data?.compare || 'ly'

	const allRows = (data?.providers || []).map(provider => ({
		provider: { ...provider, own: identity ? provider.own || null : null },
		cells: columns.map(c => cellOf(data?.cells?.[String(provider.id)]?.[c.key], stat)),
	}))
	const shownIds = visible ? new Set([...visible].map(Number)) : null
	const rows = shownIds ? allRows.filter(r => r.provider.own === 'primary' || shownIds.has(Number(r.provider.id))) : allRows

	const you = identity ? rows.find(r => r.provider.own === 'primary') || null : null
	const others = rows.filter(r => r.provider.own === 'other')
	const competitors = rows.filter(r => !r.provider.own)

	const medianRow = columns.map((_, j) => medianCell(competitors.map(r => r.cells[j]), compare))
	const stand = columns.map((_, j) => standing(rows, j, you))
	// with a market filter, "1st of 1" (you alone) says nothing: shown as —
	if (shownIds) stand.forEach(s => { if (s.total <= 1) Object.assign(s, { pos: null, gap: null }) })

	const thinCells = [...rows.flatMap(r => r.cells), ...medianRow].filter(c => c.cur && c.thin).length

	// "x of y": the competitors and the providers other than your brand, shown and in the answer
	const notYou = r => !(identity && r.provider.own === 'primary')
	const counts = {
		competitors: competitors.length, allCompetitors: allRows.filter(r => !r.provider.own).length,
		providers: rows.filter(notYou).length, allProviders: allRows.filter(notYou).length,
	}

	return { columns, rows, you, others, competitors, median: medianRow, stand, identity, compare, thinCells, filtered: !!shownIds, counts }
}

/** "Sort competitors:" — price, cheapest first · biggest drop · biggest rise · name */
export function sortRows(rows, sort = 'price') {
	const mean = xs => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
	const avgVal = r => mean(r.cells.filter(c => c.cur).map(c => c.val)) ?? Infinity
	const avgChange = r => mean(r.cells.filter(c => c.prev && c.dp !== null).map(c => c.dp)) ?? 0
	const name = r => String(r.provider.name)
	const s = [...rows]
	if (sort === 'drop') s.sort((a, b) => avgChange(a) - avgChange(b) || name(a).localeCompare(name(b)))
	else if (sort === 'rise') s.sort((a, b) => avgChange(b) - avgChange(a) || name(a).localeCompare(name(b)))
	else if (sort === 'name') s.sort((a, b) => name(a).localeCompare(name(b)))
	else s.sort((a, b) => avgVal(a) - avgVal(b) || name(a).localeCompare(name(b)))
	return s
}

export const filterRows = (rows, q) => {
	const query = String(q || '').trim().toLowerCase()
	return query ? rows.filter(r => String(r.provider.name).toLowerCase().includes(query)) : rows
}
